import {fileToBase64ObjectNoFileReader} from '@coolcolduk/util';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {useCallback, useState} from 'react';
import {User} from '@cp/model';
import {getErrorMessage} from '../../../hooks/helper/getErrorMessage';
import useSetBackgroundImage from '../../../hooks/users/useSetBackgroundImage';

export interface GeneralSettingsBackgroundProps {
  user?: User | null;
}

export default function GeneralSettingsBackground({user}: GeneralSettingsBackgroundProps) {
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const {setBackgroundImage, loading: uploadLoading} = useSetBackgroundImage();
  const backgroundUrl = user?.backgroundImageUrl;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      setBackgroundError(null);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        setBackgroundError('Please select an image file');
        return;
      }

      try {
        const base64Object = await fileToBase64ObjectNoFileReader(file);
        await setBackgroundImage(base64Object);
        setBackgroundError(null);
      } catch (err: any) {
        setBackgroundError(getErrorMessage(err));
      }
    },
    [setBackgroundImage],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        setBackgroundError('Please select an image file');
        return;
      }

      setBackgroundError(null);
      try {
        const base64Object = await fileToBase64ObjectNoFileReader(file);
        await setBackgroundImage(base64Object);
      } catch (err: any) {
        setBackgroundError(getErrorMessage(err));
      }
    },
    [setBackgroundImage],
  );

  return (
    <Box sx={{mt: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <WallpaperIcon color="primary" />
          <Typography variant="subtitle1">Background Image</Typography>
        </Box>
        {user?.backgroundImageUrl && (
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setBackgroundImage(undefined);
            }}
            disabled={uploadLoading}
            sx={{
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'white',
              },
            }}
          >
            <DeleteIcon sx={{fontSize: 18}} />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 200,
          borderRadius: 2,
          overflow: 'hidden',
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('background-upload')?.click()}
      >
        {/* Background Image */}
        {backgroundUrl && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}

        {/* Upload Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDragOver ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <CloudUploadIcon sx={{color: 'white', fontSize: 48}} />
        </Box>

        {/* Loading Overlay */}
        {uploadLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="white">
              Uploading...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden File Input */}
      <input
        id="background-upload"
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{display: 'none'}}
      />

      {/* Error Message for Background */}
      {backgroundError && (
        <Typography variant="body2" color="error" sx={{mt: 1}}>
          {backgroundError}
        </Typography>
      )}

      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
        Click or drag an image to upload a new background image
      </Typography>
    </Box>
  );
}
